import { ShieldCheck } from 'lucide-react'
import type { CrossSellingRule, TenantCommercialPolicy, VerticalPolicyView } from '@/lib/api/botSettings'
import { getVerticalProfile, normalizeToVertical } from '@/lib/verticalUI'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import CrossSellingSection from './policy/CrossSellingSection'
import PolicySectionForm from './policy/PolicySectionForm'
import VerticalRuntimeSummary from './policy/VerticalRuntimeSummary'
import { readSection, writeSectionValue, type SectionValue } from './policy/policyFormUtils'

export type PolicyViewStatus = 'loading' | 'error' | 'ready'

interface Props {
  /** Descriptores y valores efectivos que calcula el backend para el vertical del tenant. */
  view: VerticalPolicyView | null
  status: PolicyViewStatus
  policy: TenantCommercialPolicy | null
  onChange: (updated: TenantCommercialPolicy) => void
  onRetry?: () => void
}

function CardShell({ children, badge }: { children: React.ReactNode; badge?: string }) {
  return (
    <div className="space-y-6 rounded-xl border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-700 dark:bg-gray-800">
      <div className="flex items-center justify-between border-b border-gray-100 pb-4 dark:border-gray-700">
        <div className="flex items-center gap-3">
          <div className="rounded-lg bg-indigo-50 p-2.5 text-indigo-600 dark:bg-indigo-950/40 dark:text-indigo-400">
            <ShieldCheck className="h-5 w-5" aria-hidden="true" />
          </div>
          <div>
            <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">Políticas comerciales de tu negocio</h3>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Lo que escribas aquí es lo que el asistente comunicará a tus clientes. Si algo queda vacío, no lo inventa:
              lo confirma con tu equipo.
            </p>
          </div>
        </div>
        {badge && (
          <span className="inline-flex items-center rounded-full bg-indigo-100 px-2.5 py-1 text-xs font-medium text-indigo-800 dark:bg-indigo-900/40 dark:text-indigo-300">
            {badge}
          </span>
        )}
      </div>
      {children}
    </div>
  )
}

export default function CommercialPolicyCard({ view, status, policy, onChange, onRetry }: Props) {
  if (status === 'loading') {
    return (
      <CardShell>
        <div className="space-y-3" aria-busy="true" aria-label="Cargando políticas comerciales">
          <Skeleton height={56} />
          <Skeleton height={40} />
          <Skeleton height={40} />
        </div>
      </CardShell>
    )
  }

  if (status === 'error' || !view) {
    return (
      <CardShell>
        <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800 dark:border-red-900 dark:bg-red-950/30 dark:text-red-300">
          <p>No pudimos cargar las políticas de tu negocio. Tus cambios guardados no se perdieron.</p>
          {onRetry && (
            <Button type="button" variant="outline" size="sm" className="mt-3" onClick={onRetry}>
              Reintentar
            </Button>
          )}
        </div>
      </CardShell>
    )
  }

  const profile = getVerticalProfile(normalizeToVertical(view.vertical))
  const hasCommercialFields = view.descriptors.some((d) => d.group === 'commercial')
  const hasLogisticsFields = view.descriptors.some((d) => d.group === 'logistics')
  const hasDeliveryTimes = view.descriptors.some((d) => d.deliveryTimeRole)
  const section = readSection(policy, view.section)

  const updateCrossSelling = (key: string, value: SectionValue) =>
    onChange(writeSectionValue(policy, view.section, key, value))

  return (
    <CardShell badge={profile.label}>
      <VerticalRuntimeSummary runtime={view.runtime} />

      {hasCommercialFields && (
        <section aria-labelledby="policy-commercial-title" className="space-y-3">
          <h4 id="policy-commercial-title" className="text-sm font-semibold text-gray-900 dark:text-gray-100">
            Condiciones para tus clientes
          </h4>
          <PolicySectionForm view={view} policy={policy} onChange={onChange} group="commercial" />
        </section>
      )}

      {hasLogisticsFields && (
        <section aria-labelledby="policy-logistics-title" className="space-y-3">
          <h4 id="policy-logistics-title" className="text-sm font-semibold text-gray-900 dark:text-gray-100">
            Entrega, retiro y ubicación
          </h4>
          {hasDeliveryTimes && (
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Si también completas los tiempos en «Despacho y envíos», prevalecen los de esa sección.
            </p>
          )}
          <PolicySectionForm view={view} policy={policy} onChange={onChange} group="logistics" />
        </section>
      )}

      {!hasCommercialFields && !hasLogisticsFields && !view.crossSelling && (
        <p className="rounded-lg bg-gray-50 p-4 text-sm text-gray-600 dark:bg-gray-900/50 dark:text-gray-400">
          Este tipo de negocio no necesita condiciones comerciales adicionales.
        </p>
      )}

      {view.crossSelling && (
        <div className="border-t border-gray-100 pt-2 dark:border-gray-700">
          <CrossSellingSection
            enabled={section['crossSellingEnabled'] === true}
            closingQuestion={typeof section['closingQuestionOverride'] === 'string' ? section['closingQuestionOverride'] : ''}
            rules={Array.isArray(section['crossSellingRules']) ? (section['crossSellingRules'] as CrossSellingRule[]) : []}
            onToggleEnabled={(enabled) => updateCrossSelling('crossSellingEnabled', enabled)}
            onChangeClosingQuestion={(value) => updateCrossSelling('closingQuestionOverride', value)}
            onChangeRules={(rules) => updateCrossSelling('crossSellingRules', rules)}
          />
        </div>
      )}
    </CardShell>
  )
}
